import { useState } from 'react'
import toString from 'lodash/toString'
import { FileWarning } from 'lucide-react'
import { NOT_PREVIEWABLE_MESSAGE } from '@klear/upload/content-type'
import { buildDocumentFileUrl } from '@klear/upload/file-url'
import { PdfDocumentView, type QuoteHighlight } from '@/app/document-review/pdf-document-view'
import { isBrowserRenderable, isImageDocument, isPdfDocument } from '@/app/document-review/source-document-file'

export type DocumentPaneProps = {
  documentId: number
  uploadedFilename: string | null
  highlights: readonly QuoteHighlight[]
  activeKey: string | null
}

export function DocumentPane({ documentId, uploadedFilename, highlights, activeKey }: DocumentPaneProps) {
  const url = buildDocumentFileUrl(toString(documentId))

  return (
    <div className="bg-card flex h-full min-h-0 flex-col overflow-hidden rounded-lg border">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2">
        <span className="truncate text-sm font-medium">{uploadedFilename ?? 'Source document'}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-muted-foreground hover:text-foreground shrink-0 text-xs underline"
        >
          {isBrowserRenderable(uploadedFilename) ? 'Open in a new tab' : 'Download'}
        </a>
      </div>
      <div className="min-h-0 flex-1">
        <PaneBody
          key={url}
          url={url}
          uploadedFilename={uploadedFilename}
          highlights={highlights}
          activeKey={activeKey}
        />
      </div>
    </div>
  )
}

function PaneBody({
  url,
  uploadedFilename,
  highlights,
  activeKey
}: {
  url: string
  uploadedFilename: string | null
  highlights: readonly QuoteHighlight[]
  activeKey: string | null
}) {
  const [pdfFailed, setPdfFailed] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)

  if (!isBrowserRenderable(uploadedFilename)) {
    return <PaneNotice title="This file can't be shown here" detail={NOT_PREVIEWABLE_MESSAGE} />
  }

  if ((isPdfDocument(uploadedFilename) || imageFailed) && !pdfFailed) {
    return (
      <PdfDocumentView url={url} highlights={highlights} activeKey={activeKey} onLoadError={() => setPdfFailed(true)} />
    )
  }

  if (isImageDocument(uploadedFilename) && !imageFailed) {
    return (
      <div className="flex h-full items-center justify-center p-2">
        <img
          src={url}
          alt={uploadedFilename ?? 'Source document'}
          className="max-h-full max-w-full object-contain"
          onError={() => setImageFailed(true)}
        />
      </div>
    )
  }

  return <iframe src={url} className="h-full w-full border-0" title={uploadedFilename ?? 'Source document'} />
}

function PaneNotice({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <FileWarning className="text-muted-foreground h-8 w-8" aria-hidden />
      <span className="text-sm font-medium">{title}</span>
      <span className="text-muted-foreground max-w-sm text-sm">{detail}</span>
    </div>
  )
}
